import { Component, Input, signal, computed, inject, ViewChild, ChangeDetectorRef } from '@angular/core';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { CommonModule } from '@angular/common';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatChipsModule } from '@angular/material/chips';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatOptionModule } from '@angular/material/core';
import { ReactiveFormsModule, FormControl, FormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { trigger, transition, style, animate, keyframes, state, query } from '@angular/animations';
import { MatDialog } from '@angular/material/dialog';
import { GroupEditorComponent } from '../dialogs/group-editor/group-editor.component';
import { Observable, startWith, map, combineLatest, filter, firstValueFrom, first, BehaviorSubject, Subject, Subscription, debounceTime, tap } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { ActivatedRoute, Data, Params, Router, RouterModule } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { ConfirmComponent } from '../../dialogs/confirm/confirm.component';
import { FilterEditorComponent } from '../../components/filter-editor/filter-editor.component';
import { FilterChip } from '../../models/filter-chip';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { GroupsService } from '../../services/groups.service';
import { Group } from '../../models/group';
import { GroupMembershipEditorComponent } from '../dialogs/group-membership-editor/group-membership-editor.component';
import { GroupsPickerDialogComponent } from '../dialogs/groups-picker-dialog/groups-picker-dialog.component';
import { Effort } from '../../models/effort';
import { EffortsService } from '../../services/efforts.service';
import { User } from '../../models/user';
import { UsersService } from '../../services/user.service';
import { GroupMembersService } from '../../services/group-members.service';
import { FilterChipUtilityService } from '../../services/filter-chip-utility.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

@Component({
  selector: 'app-group-management',
  standalone: true,
  templateUrl: './group-management.component.html',
  styleUrls: ['./group-management.component.scss'],
  imports: [
    CommonModule,
    MatTableModule,
    MatExpansionModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatChipsModule,
    MatAutocompleteModule,
    MatOptionModule,
    ReactiveFormsModule,
    MatSelectModule,
    FormsModule,
    MatButtonModule,
    FilterEditorComponent,
    RouterModule,
    MatSortModule,
    MatPaginatorModule,
    MatProgressSpinnerModule
  ],
  animations: [
    // trigger('fadeAnimation', [
    //   state('hidden', style({ opacity: '0' })),
    //   state('shown', style({ opacity: '1'})),
    //   transition('* => *', animate('225ms ease', keyframes([
    //     style({ opacity: 0, backgroundColor: 'white', offset: 0 }),
    //     style({ opacity: 1, backgroundColor: '#f16', offset: 0.5 }),
    //     style({ opacity: 0, backgroundColor: 'white', offset: 1 })
    //   ])))
    // ]),
    trigger('fadeAnimation', [
      transition(':enter', [
        style({ opacity: '0' }),
        animate('0.2s 300ms ease-in', style({ opacity: '1' }))
      ]),
      transition(':leave', [
        style({ opacity: '1' }),
        animate('0.2s ease-out', style({ opacity: '0' }))
      ])
    ]),
    trigger('fadeExpand', [
      state('false', style({ display: 'none' })),
      state('true', style({ display: '*' })),
      transition('* => true', [
        animate('500ms ease-in', keyframes([style({ display: '*', offset: 0 })])),
        query('.group-editor', [
          style({
            opacity: 0,
            height: '0px',
            transform: 'translateY(-10px)'
          }),
          animate('500ms ease-in', style({ opacity: 1, height: '*' }))
        ])
      ]),
      transition('true => false', [
        query('.group-editor', [
          style({
            opacity: 1,
            height: '*'
          }),

          animate('500ms ease-out', style({ opacity: 0, height: '0px', transform: 'translateY(-10px)' }))
        ])
      ]),
      // transition('true => false', [
      //     animate('500ms ease-out')
      // ])
    ]),
    trigger('rowAnimation', [
      state('false', style({ display: 'none' })),
      transition('0 => 1', [
        animate('500ms ease-in', keyframes([style({ display: '*', opacity: 1, height: '*', offset: 0 })]))
      ]),
      transition('1 => 0', [
        animate('500ms ease-out', keyframes([style({ display: 'none', offset: 1.0 })]))
      ])
    ])
  ]
})
export class GroupManagementComponent {
  //@Input() groups: Group[] = [{ id: 1, title: 'Bonus round', tags: ["awesome"], users: [] }];
  @Input() allTags: string[] = [];
  @Input() allMembers: string[] = [];
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  effortSubscription?: Subscription;
  effort?: Effort;

  groups!: Group[];
  filterControl = new FormControl('');
  filters: FilterChip[] = [];
  filterOptions: string[] = ['key:', 'name:', 'member:', 'effort:'];
  filteredOptions$: Observable<string[]>;
  selectedFilters: Subject<FilterChip[]> = new BehaviorSubject<FilterChip[]>([]);
  _loading = new BehaviorSubject<boolean>(false);
  isLoading = this._loading.pipe(debounceTime(environment.spinnerDelay));
  readonly groupsService = inject(GroupsService);
  readonly effortsService = inject(EffortsService);
  readonly groupMembersService = inject(GroupMembersService);
  readonly usersService = inject(UsersService);
  readonly http = inject(HttpClient);
  readonly activatedRoute = inject(ActivatedRoute);
  readonly router = inject(Router);
  readonly data = this.activatedRoute.data;
  readonly dialog = inject(MatDialog);
  readonly cdr = inject(ChangeDetectorRef);
  readonly filterUtility = inject(FilterChipUtilityService);
  readonly separatorKeysCodes: number[] = [ENTER, COMMA];
  readonly titleFilter = new FormControl('');
  readonly departmentFilter = new FormControl('');
  readonly tagFilter = new FormControl('');
  readonly expandedGroup = signal<number | null>(null);
  currentTag = '';
  readonly dataSource = new MatTableDataSource();
  subscription: any;
  activeColumns = ['name', 'owners', 'efforts'];
  userSubscription?: Subscription;
  groupMembersSubscr?: Subscription;
  effortsSubscr?: Subscription;
  paramsSubscr?: Subscription;
  pagiSubscr?: Subscription;
  member: User | undefined;
  totalDepts = 0;
  private _dataChangedSubject = new Subject<FilterChip[]>();
  private dataChanged$ = this._dataChangedSubject.asObservable();
    readonly chipHelpers = {
    "name": "Filters by Department's name",
    "key": "Filters by Department's unique key",
    "effort": "Filters by Efforts belonging to the Department",
    "member": "Filters by Department's owners/members",
    "member.hawkid": "Filters by whether a member has particular Hawk Id"
  };
  readonly chipPossVals: { [key: string]: string[] | ((v: string) => Promise<string[] | string[][]>) } = {
    'member.hawkid': (value: string) =>
      firstValueFrom(this.usersService.filterUsers([{ chip: 'hawkid', value }]).pipe(map((o => o.users.map(u => [u.hawkId, `${u.displayName} (${u.hawkId})`]))))),
    'member': (value: string) =>
      firstValueFrom(this.usersService.filterUsers([{ chip: null, value }]).pipe(map((o => o.users.map(u => [u.hawkId, `${u.displayName} (${u.hawkId})`]))))),
    "effort": (value: string) =>
      firstValueFrom(this.effortsService.filterEfforts([{ chip: null, value }]).pipe(map((o => o.efforts.map(u => [u.key, `${u.title} (${u.key})`]))))),
  }

  constructor() {
    this.filteredOptions$ = this.filterControl.valueChanges.pipe(
      startWith(''),
      map(value => this.filterOptions.filter(option => value && option.startsWith(value)))
    );
  }

  ngAfterViewInit() {
    this.paramsSubscr = combineLatest([
      this.activatedRoute.params.pipe(filter(a => !!a)),
      this.activatedRoute.queryParams.pipe(filter(a => !!a)),
      this.activatedRoute.data.pipe(filter(a => !!a))
    ]).subscribe(this.handleParams.bind(this));
    this.pagiSubscr = combineLatest([
      this.dataChanged$.pipe(debounceTime(100)),
      this.sort.sortChange.pipe(startWith({ active: this.sort.active, direction: this.sort.direction })),
      this.paginator.page.pipe(startWith({ pageSize: this.paginator.pageSize, pageIndex: this.paginator.pageIndex })),
    ]).pipe(debounceTime(100)).subscribe(([filters, sort, page]) => {
        this._loading.next(true);
        this.subscription?.unsubscribe();
        this.subscription = this.groupsService.filterGroups(filters, page.pageSize, page.pageIndex, sort.active, sort.direction).subscribe(resp => {
          this._loading.next(false);
          this.filters = filters;
          this.dataSource.data = resp.groups;
          this.totalDepts = resp.total;
        });
      });
    this.groupMembersSubscr = this.groupMembersService.sourceUpdated$.subscribe(_ => this._dataChangedSubject.next(this.filters ?? []));
    this.effortsSubscr = this.effortsService.sourceUpdated$.subscribe(_ => this._dataChangedSubject.next(this.filters ?? []));
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
    this.groupMembersSubscr?.unsubscribe();
    this.effortsSubscr?.unsubscribe();
    this.effortSubscription?.unsubscribe();
    this.userSubscription?.unsubscribe();
    this.paramsSubscr?.unsubscribe();
    this.pagiSubscr?.unsubscribe();
    this.paramsSubscr?.unsubscribe();
  }

  clearFilter(...chips: string[]) { this.selectedFilters.next(this.filters?.filter(f => f.chip && !chips.includes(f.chip)) ?? []); return false; }

  handleParams([params, p, data]: [Params, Params, Data]) {
    this.groups = (<{ groups: Group[] }>data).groups;
    const filters = [];
    if (p["filter.key"]) filters.push({ chip: 'key', value: p['filter.key'] });
    if (p["filter.name"]) filters.push({ chip: 'name', value: p["filter.name"] });
    if (p["filter.member"]) filters.push({ chip: 'member', value: p["filter.member"] });
    if (p["filter.member.hawkid"]) filters.push({ chip: 'member.hawkid', value: p["filter.member.hawkid"] });
    if (p["filter.effort"]) filters.push({ chip: 'effort', value: p["filter.effort"] });
    if (p["filter.keywords"]) filters.push({ chip: null, value: p["filter.keywords"] });
    this.paginator.firstPage();
    this.filters = filters;
    this.selectedFilters.next(filters);
    this._dataChangedSubject.next(filters);
  }

  toggleExpand(groupId: number) {
    this.expandedGroup.set(this.expandedGroup() === groupId ? null : groupId);
  }

  removeMember(group: Group, user: string) {
    group.members = group.members.filter(m => m.user.hawkId !== user);
  }

  async openEditor(group: Group | null, isNew: boolean): Promise<void> {
    this.dialog.open(GroupEditorComponent, {
      width: '50vw', minWidth: '500px', maxWidth: '720px',
      data: { isNew, group }
    }).afterClosed().subscribe(async result => {
      if (result && !result.isNew && result.group) {
        this._loading.next(true);
        console.info(this.openEditor.name, 'Updated Group:', result.group);
        this.groupsService.updateGroup(result.group);
      }

      if (result.isNew && result.group) {
        this._loading.next(true);
        this.groupsService.addGroup(result.group);
      }
    });
  }

  groupMembersSummary(group: Group) {
    if (group.members.length === 0) return "None";
    let first2or3 = group.members.slice(0, group.members.length == 3 ? 3 : 2).map(m => m.user.hawkId).join(", ");
    if (group.members.length > 3) {
      first2or3 += `, and ${group.members.length - 2} others.`;
    }
    return first2or3;
  }

  async openMembershipEditor(group: Group | null, isNew: boolean): Promise<void> {
    this.dialog.open(GroupMembershipEditorComponent, {
      width: '50vw', minWidth: '500px', maxWidth: '720px',
      data: { group }
    }).afterClosed().subscribe(async result => {
      if (result && !result.isNew && result.group) {
        this._loading.next(true);
        console.info(this.openEditor.name, 'Updated Group:', result.group);
        this.groupsService.updateGroup(result.group);
      }

      if (result.isNew && result.group) {
        this._loading.next(true);
        this.groupsService.addGroup(result.group);
      }
    });
  }

  async openGroupDetails(group: Group) {
    console.info(this.openGroupDetails.name, group.id, "group id")
    this.router.navigate(["/users"], { queryParams: { 'filter.group': group.key ?? group.id, } });
  }

  async deleteGroup(group: Group) {
    console.info(this.deleteGroup, group);
    const message = `Are you sure you want to delete '${group.name}'?`;
    this.dialog.open(ConfirmComponent, { width: '400px', data: { title: "Are you sure?", message } })
      .afterClosed().subscribe(async result => {
        if (result) {
          this._loading.next(true);
          this.groupsService.deleteGroup(group);
        }
      });
  }

  filterChange(filters: FilterChip[]): void {
    console.log("Filter changed", filters);
    this._loading.next(false);
    this.filterUtility.updateUrlWithFilters(filters, '/departments');
    const effortFilter = filters.find(f => f.chip == 'effort');
    const memberFilters = filters.filter(f => f.chip != undefined && 'member.hawkid member'.split(' ').includes(f.chip));
    this._dataChangedSubject.next(filters);
    const estCol = "effort-specific-tools";
    const resetEffortSpecFn = () => {
      this.effort = undefined;
      this.activeColumns = this.activeColumns.filter(ac => ac != estCol);
    };
    if (effortFilter) {
      this.effortSubscription?.unsubscribe();
      this.effortSubscription = this.effortsService.filterEfforts([{ chip: 'key', value: effortFilter.value }]).subscribe(res => {
        if (res.total == 1) {
          this.effort = res.efforts[0];
          this.activeColumns = this.activeColumns.find(ac => ac == estCol) ? this.activeColumns : [...this.activeColumns, estCol]
        } else {
          resetEffortSpecFn();
        }
      });
    } else {
      resetEffortSpecFn();
    }

    const mstCol = "member-specific-tools";
    const resetMemberSpecFn = () => {
      this.member = undefined;
      this.activeColumns = this.activeColumns.filter(ac => ac != mstCol);
    };
    if (memberFilters.length) {
      const chips = <any>{ 'member.hawkid': 'hawkid', 'member': null };
      const translated = memberFilters.filter(c => c.chip).map(c => { return { chip: chips[c.chip!], value: c.value }; });
      this.userSubscription?.unsubscribe();
      this.userSubscription = this.usersService.filterUsers(translated, 10, 0).subscribe(resp => {
        if (resp.total == 1) {
          const members = resp.users;
          this.member = members[0];
          this.activeColumns = this.activeColumns.find(ac => ac == mstCol) ? this.activeColumns : [...this.activeColumns, mstCol]
        } else {
          resetMemberSpecFn();
        }
      });
    } else {
      resetMemberSpecFn();
    }
  }

  async openAddToEffortDialog(): Promise<void> {
    if (!this.effort) {
      console.error("Shouldn't be able to get here without a group.");
    }

    this.dialog.open(GroupsPickerDialogComponent, {
      width: '50vw', minWidth: '500px', maxWidth: '720px',
      data: { effort: this.effort }
    }).afterClosed().subscribe(async groups => {
      if (groups?.length) {
        console.info(this.openAddToEffortDialog.name, 'Updated Group:', groups);
        this.effortsService.addEffortGroups(this.effort!, groups);
      } else {
        console.info("No groups selected.");
      }
    });
  }

  async openAddGroupsToMember() {
    if (!this.member) {
      console.error("Shouldn't be able to get here without a member.");
    }

    this.dialog.open(GroupsPickerDialogComponent, {
      width: '50vw', minWidth: '500px', maxWidth: '720px',
      data: { title: `Add groups for user '${this.member?.displayName}'` }
    }).afterClosed().subscribe(async groups => {
      if (groups?.length && this.member) {
        console.info(this.openAddToEffortDialog.name, groups);
        this.groupMembersService.addUserToGroups(groups, this.member);
      } else {
        console.info("No groups or member selected.");
      }
    });
  }

  removeGroupFromEffort(group: Group, effort: Effort) {
    console.info(this.removeGroupFromEffort.name);
    const data = { message: `Remove group '${group.name}' from effort '${effort.title}'?` };
    this.dialog.open(ConfirmComponent, { data }).afterClosed()
      .subscribe(yes => {
        if (yes) this.effortsService.removeGroupEffort(group.id, effort.id);
      })
  }

  removeMemberFromGroup(member: User, group: Group) {
    console.info(this.removeMemberFromGroup.name);
    if (!member || !group) {
      console.error("Member or group not supplied.");
    }

    const data = { message: `Remove member '${member.displayName}' from group '${group.name}'?` };
    this.dialog.open(ConfirmComponent, { data }).afterClosed()
      .subscribe(yes => {
        if (yes) this.groupMembersService.deleteGroupMembership(group.id, member.id);
      })
  }
}
